import './ValidatedTextInput.scss';
import { TranslationKey } from '@Localization';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent, ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { S_FieldError } from '../../design/errors/fieldError/S_FieldError';
import { S_TextInput } from '../../design/inputs/textInput/S_TextInput';
import { S_FieldLabel } from '../../design/labels/fieldLabel/S_FIeldLabel';
import { ShowHidePassword } from './ShowHidePasswordIcon';

type InputType = 'text' | 'email' | 'file' | 'number' | 'password';

interface IProps {
  id?: string;
  labelText?: string;
  value?: string | number;
  type?: InputType;
  maxLength?: number;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
  onPressEnter?: () => void;
  isValid?: boolean;
  feedbacks?: TranslationKey[];
  feedbacksParams?: Record<string, string | number>;
  isRequired?: boolean;
  className?: string;
  readOnly?: boolean;
  enablePreview?: boolean;
  disabled?: boolean;
  autofocus?: boolean;
  onPressEscape?: () => void;
  children?: ReactNode;
  autoComplete?: string;
  defaultValue?: string | number | readonly string[] | undefined;
}

export const ValidatedTextInput = observer((props: IProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [type, setType] = useState<InputType>(props.type ?? 'text');

  useEffect(() => {
    if (props.autofocus) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, []);

  const handleKeyPress = (target: KeyboardEvent): void => {
    if (target.key === 'Enter' && props.onPressEnter) {
      target.preventDefault();
      props.onPressEnter();
    }
  };

  const handleKeyDown = (target: KeyboardEvent): void => {
    if (target.key === 'Escape' && props.onPressEscape) {
      target.preventDefault();
      props.onPressEscape();
    }
  };

  const togglePasswordVisibility = (): void => {
    setType((prevState) => (prevState === 'password' ? 'text' : 'password'));
  };

  const maxLength = props.maxLength === undefined ? 100 : props.maxLength;

  return (
    <div className='validated-text-input'>
      {props.labelText && (
        <S_FieldLabel
          isRequired={props.isRequired}
          text={props.labelText}
        />
      )}
      <div className='validated-text-input-wrapper'>
        <S_TextInput
          id={props.id}
          defaultValue={props.defaultValue}
          value={props.value !== null ? props.value : ''}
          error={props.isValid === false}
          type={type}
          placeholder={props.placeholder}
          onChange={props.onChange}
          onBlur={props.onBlur}
          maxLength={maxLength}
          min={props.min}
          max={props.max}
          step={props.step}
          required={props.isRequired}
          className={props.className}
          readOnly={props.readOnly}
          onKeyPress={handleKeyPress}
          onKeyDown={handleKeyDown}
          disabled={props.disabled}
          innerRef={inputRef}
          autoComplete={props.autoComplete}
          endDecorator={
            props.enablePreview && (
              <ShowHidePassword
                isPasswordVisible={type === 'password'}
                onClick={togglePasswordVisibility}
              />
            )
          }
        />
      </div>
      {props.children}
      {props.feedbacks && (
        <S_FieldError
          feedbacks={props.feedbacks}
          feedbacksParams={props.feedbacksParams}
        />
      )}
    </div>
  );
});
